const AUDIO_MIME_TYPES = ['audio/mp4', 'audio/aac', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus']
const VIDEO_MIME_TYPES = ['video/mp4', 'video/mp4;codecs=avc1', 'video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm']

const EXTENSIONS = {
    'audio/mp4': 'm4a',
    'audio/aac': 'aac',
    'audio/webm': 'webm',
    'audio/ogg': 'ogg',
    'audio/wav': 'wav',
    'audio/mpeg': 'mp3',
    'video/mp4': 'mp4',
    'video/webm': 'webm',
    'video/quicktime': 'mov'
}

export const DEFAULT_AUDIO_MIME_TYPE = 'audio/mp4'
export const DEFAULT_VIDEO_MIME_TYPE = 'video/mp4'

export const MAX_AUDIO_SECONDS = 180
export const MAX_VIDEO_SECONDS = 30
export const VIDEO_WARNING_SECONDS = 5

export const AUDIO_CONSTRAINTS = {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true
}

export const VIDEO_CONSTRAINTS = {
    width: { ideal: 720 },
    height: { ideal: 1280 },
    frameRate: { ideal: 30 }
}

export const AUDIO_RECORDER_OPTIONS = { audioBitsPerSecond: 64000 }
export const VIDEO_RECORDER_OPTIONS = { videoBitsPerSecond: 2500000, audioBitsPerSecond: 128000 }

const UNIVERSAL_AUDIO_TYPES = ['audio/mp4', 'audio/aac', 'audio/mpeg', 'audio/wav', 'audio/x-m4a']
const WAV_SAMPLE_RATE = 16000

function supportedMimeType(candidates) {
    if (typeof MediaRecorder === 'undefined') return ''
    return candidates.find(type => MediaRecorder.isTypeSupported(type)) || ''
}

export function supportedAudioMimeType() {
    return supportedMimeType(AUDIO_MIME_TYPES)
}

export function supportedVideoMimeType() {
    return supportedMimeType(VIDEO_MIME_TYPES)
}

export function mediaExtension(mimeType) {
    const base = (mimeType || '').split(';')[0].trim()
    if (EXTENSIONS[base]) return EXTENSIONS[base]
    return base.startsWith('video/') ? 'mp4' : 'm4a'
}

const AUDIO_MIME_BY_EXTENSION = {
    m4a: 'audio/mp4',
    mp4: 'audio/mp4',
    aac: 'audio/aac',
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    webm: 'audio/webm',
    ogg: 'audio/ogg'
}

export function canPlayAudioUrl(url) {
    if (!url || typeof document === 'undefined') return true
    const extension = url.split('?')[0].split('.').pop().toLowerCase()
    const mimeType = AUDIO_MIME_BY_EXTENSION[extension]
    if (!mimeType) return true
    return !!document.createElement('audio').canPlayType(mimeType)
}

export function canRecordMedia() {
    return !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined'
}

export function isUniversalAudioType(mimeType) {
    const base = (mimeType || '').split(';')[0].trim().toLowerCase()
    return UNIVERSAL_AUDIO_TYPES.includes(base)
}

function encodeWav(samples, sampleRate) {
    const buffer = new ArrayBuffer(44 + samples.length * 2)
    const view = new DataView(buffer)
    const writeText = (offset, text) => {
        for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i))
    }

    writeText(0, 'RIFF')
    view.setUint32(4, 36 + samples.length * 2, true)
    writeText(8, 'WAVE')
    writeText(12, 'fmt ')
    view.setUint32(16, 16, true)
    view.setUint16(20, 1, true)
    view.setUint16(22, 1, true)
    view.setUint32(24, sampleRate, true)
    view.setUint32(28, sampleRate * 2, true)
    view.setUint16(32, 2, true)
    view.setUint16(34, 16, true)
    writeText(36, 'data')
    view.setUint32(40, samples.length * 2, true)

    let offset = 44
    for (let i = 0; i < samples.length; i++) {
        const sample = Math.max(-1, Math.min(1, samples[i]))
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true)
        offset += 2
    }

    return new Blob([view], { type: 'audio/wav' })
}

export async function toUniversalAudio(blob) {
    if (!blob || isUniversalAudioType(blob.type)) return blob

    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    const OfflineContextClass = window.OfflineAudioContext || window.webkitOfflineAudioContext
    if (!AudioContextClass || !OfflineContextClass) return blob

    let context = null
    try {
        context = new AudioContextClass()
        const decoded = await context.decodeAudioData(await blob.arrayBuffer())
        const frames = Math.ceil(decoded.duration * WAV_SAMPLE_RATE)
        if (!frames) return blob

        const offline = new OfflineContextClass(1, frames, WAV_SAMPLE_RATE)
        const source = offline.createBufferSource()
        source.buffer = decoded
        source.connect(offline.destination)
        source.start()

        const rendered = await offline.startRendering()
        return encodeWav(rendered.getChannelData(0), WAV_SAMPLE_RATE)
    } catch (e) {
        console.error('[recorder] audio conversion failed', e)
        return blob
    } finally {
        if (context) context.close().catch(() => {})
    }
}
